import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  InfoBox,
  Input,
  Label,
  LoadingState,
  Toast,
  kstMonthDayStr,
  kstTimeStr,
} from '@biznomi/ui-react'
import {
  getNfcSpots,
  createNfcSpot,
  updateNfcSpot,
  deactivateNfcSpot,
  type NfcSpot,
} from '@/lib/api/admin'

/**
 * NFC 근무지(태그판) 관리 — ADMIN 전용.
 *
 * ★ nfcUid 가 비어 있으면 그 근무지에서는 **아무 태그도 인식되지 않는다.**
 *   직원은 "폰을 댔는데 아무 일도 안 일어난다"고만 느낀다. 그래서 목록에서
 *   가장 먼저 보이게 하고, 비어 있으면 경고로 표시한다.
 */

/** 서버가 저장할 때 정규화하지만, 입력 즉시 보여주려고 화면에서도 같은 모양으로 만든다 */
function formatUid(raw: string): string {
  const hex = raw.toUpperCase().replace(/[^A-F0-9]/g, '')
  return hex.replace(/(.{2})(?=.)/g, '$1:')
}

interface EditState {
  spot: NfcSpot | null // null = 새로 만들기
  name: string
  location: string
  nfcUid: string
}

const EMPTY: EditState = { spot: null, name: '', location: '', nfcUid: '' }

export default function NfcSpots() {
  const qc = useQueryClient()
  const [edit, setEdit] = useState<EditState | null>(null)
  const [deactivating, setDeactivating] = useState<NfcSpot | null>(null)
  const [saveError, setSaveError] = useState('')
  const [toast, setToast] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'nfc-spots'],
    queryFn: async () => {
      const res = await getNfcSpots()
      if (!res.success) throw new Error(res.error ?? '근무지를 불러오지 못했습니다')
      return res.data ?? []
    },
  })

  const save = useMutation({
    mutationFn: async (e: EditState) => {
      if (!e.spot) {
        return createNfcSpot({ name: e.name.trim(), location: e.location.trim() || undefined })
      }
      return updateNfcSpot(e.spot.id, {
        name: e.name.trim(),
        location: e.location.trim(),
        nfcUid: e.nfcUid.trim() ? formatUid(e.nfcUid) : null,
      })
    },
    onSuccess: (res) => {
      if (!res.success) {
        // 다이얼로그를 닫지 않는다 — 고칠 수 있는 문제니 입력값을 남겨둔다
        setSaveError(res.error ?? '저장에 실패했습니다')
        return
      }
      setEdit(null)
      setSaveError('')
      setToast('저장했습니다')
      qc.invalidateQueries({ queryKey: ['admin', 'nfc-spots'] })
    },
  })

  const deactivate = useMutation({
    mutationFn: (id: string) => deactivateNfcSpot(id),
    onSuccess: (res) => {
      setDeactivating(null)
      setToast(res.success ? '비활성화했습니다' : (res.error ?? '실패했습니다'))
      qc.invalidateQueries({ queryKey: ['admin', 'nfc-spots'] })
    },
  })

  if (isLoading) return <LoadingState />
  if (error) return <InfoBox tone="danger">{(error as Error).message}</InfoBox>

  const spots = data ?? []
  const missingUid = spots.filter((s) => s.isActive && !s.nfcUid).length

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold">근무지</h1>
        <Button size="sm" onClick={() => setEdit(EMPTY)}>
          근무지 추가
        </Button>
      </header>

      {missingUid > 0 && (
        <InfoBox tone="warning" title={`태그판 번호가 없는 근무지 ${missingUid}곳`}>
          번호가 비어 있으면 그 자리에서는 폰을 대도 인식되지 않습니다. 직원은 이유를 알 수 없으니
          카드를 설치했다면 번호를 채워 주세요.
        </InfoBox>
      )}

      {spots.length === 0 ? (
        <div className="rounded-card bg-card">
          <EmptyState
            title="등록된 근무지가 없습니다."
            hint="NFC 카드를 설치한 자리를 등록하세요."
            icon="inbox"
          />
        </div>
      ) : (
        <ul className="space-y-2">
          {spots.map((s) => (
            <li key={s.id} className="rounded-card bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-semibold">{s.name}</span>
                    {!s.isActive && <Badge variant="default">비활성</Badge>}
                    {s.isActive && !s.nfcUid && <Badge variant="warning">번호 없음</Badge>}
                  </div>

                  {s.location && <p className="text-sm text-slate-500">{s.location}</p>}

                  <p className="font-mono text-xs text-slate-400">
                    {s.nfcUid ?? '태그판 번호 미등록'}
                  </p>

                  <p className="text-xs text-slate-400">
                    오늘 {s.todayTagCount}회
                    {s.lastTag && (
                      <>
                        {' · 마지막 '}
                        {kstMonthDayStr(s.lastTag.timestamp)} {kstTimeStr(s.lastTag.timestamp)}
                        {` (${s.lastTag.employeeName})`}
                      </>
                    )}
                  </p>
                </div>

                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setEdit({
                        spot: s,
                        name: s.name,
                        location: s.location ?? '',
                        nfcUid: s.nfcUid ?? '',
                      })
                    }
                  >
                    수정
                  </Button>
                  {s.isActive && (
                    <Button size="sm" variant="ghost" onClick={() => setDeactivating(s)}>
                      비활성화
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={edit !== null}
        onOpenChange={(o) => {
          if (!o) {
            setEdit(null)
            setSaveError('')
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{edit?.spot ? '근무지 수정' : '근무지 추가'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="spot-name">이름</Label>
              <Input
                id="spot-name"
                autoFocus
                value={edit?.name ?? ''}
                onChange={(e) => setEdit((s) => (s ? { ...s, name: e.target.value } : s))}
                placeholder="예: 본관 1층 출입구"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="spot-loc">위치 (선택)</Label>
              <Input
                id="spot-loc"
                value={edit?.location ?? ''}
                onChange={(e) => setEdit((s) => (s ? { ...s, location: e.target.value } : s))}
                placeholder="예: 엘리베이터 옆"
              />
            </div>

            {/* 추가 직후엔 카드가 아직 없을 수 있어 수정에서만 받는다 */}
            {edit?.spot && (
              <div className="space-y-1.5">
                <Label htmlFor="spot-uid">태그판 번호</Label>
                <Input
                  id="spot-uid"
                  value={edit.nfcUid}
                  onChange={(e) => setEdit((s) => (s ? { ...s, nfcUid: e.target.value } : s))}
                  placeholder="04:62:C3:5A:08:17:91"
                  className="font-mono"
                />
                <p className="text-xs leading-relaxed text-slate-400">
                  카드 뒷면 번호이거나, 직원 앱으로 한 번 태그하면 기록에 남습니다. 대소문자·구분기호는
                  자동으로 맞춰집니다.
                </p>
              </div>
            )}
          </div>

          {saveError && (
            <p role="alert" className="mt-3 rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
              {saveError}
            </p>
          )}

          <DialogFooter>
            <Button variant="secondary" className="flex-1" onClick={() => { setEdit(null); setSaveError('') }}>
              취소
            </Button>
            <Button
              className="flex-1"
              disabled={!edit?.name.trim()}
              loading={save.isPending}
              onClick={() => { setSaveError(''); if (edit) save.mutate(edit) }}
            >
              저장
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deactivating !== null}
        onOpenChange={(o) => !o && setDeactivating(null)}
        title="이 근무지를 비활성화할까요?"
        message={`${deactivating?.name ?? ''}\n\n지난 기록은 그대로 남습니다. 이 자리에서 새로 태깅만 되지 않습니다.`}
        confirmText="비활성화"
        destructive
        loading={deactivate.isPending}
        onConfirm={() => deactivating && deactivate.mutate(deactivating.id)}
      />

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}
